import { useCallback, useMemo } from 'react'
import { useAsync } from 'react-async-hook'
import {
  auctionManagerKey,
  init as initAuctionManager,
  meowMetadataKey,
  stakeMetadataKey,
  stakeRecieptKey,
} from '@dewicats/auction-manager-sdk'
import { useSolana } from '@dewicats/connect-button'
import { DEWICATS_COLLECTION } from '@utils/constants'
import { PublicKey, Transaction } from '@solana/web3.js'
import { bulkSendRawTransactions } from '@helium/spl-utils'
import { batchInstructionsToTxsWithPriorityFee } from '@helium/spl-utils'
import {
  findTokenRecordPda,
  fetchTokenRecord,
} from '@metaplex-foundation/mpl-token-metadata'
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults'
import { publicKey } from '@metaplex-foundation/umi'
import { getAssociatedTokenAddress } from '@solana/spl-token'
import { useToast } from '@hooks/use-toast'

const useStaking = ({ nft }: { nft: PublicKey }) => {
  const { anchorProvider } = useSolana()
  const { toast } = useToast()

  const { result: auctionProgram } = useAsync(() => {
    return initAuctionManager(anchorProvider)
  }, [anchorProvider])

  const [auctionKey] = useMemo(
    () => auctionManagerKey(DEWICATS_COLLECTION, 'dewicats'),
    [],
  )

  const umi = useMemo(
    () => createUmi(process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST),
    [],
  )

  const { result: hasDelegateAuthority } = useAsync(async () => {
    const ata = await getAssociatedTokenAddress(
      nft,
      new PublicKey(anchorProvider.publicKey.toBase58()),
      true,
    )

    const tRecord = await findTokenRecordPda(umi, {
      mint: publicKey(nft.toBase58()),
      token: publicKey(ata.toBase58()),
    })

    const tokenRecord = await fetchTokenRecord(umi, tRecord)

    return (tokenRecord.delegate as any)?.value !== auctionKey.toBase58()
  }, [anchorProvider])

  const [stakeMetadata] = useMemo(
    () => stakeMetadataKey(nft, auctionKey),
    [nft, auctionKey],
  )

  const {
    result: stakeMetadataAcc,
    loading: stakeMetadataLoading,
    execute: fetchStakeMetadata,
  } = useAsync(() => {
    return auctionProgram.account.stakeMetadataV0.fetch(stakeMetadata)
  }, [anchorProvider, stakeMetadata, auctionProgram])

  const isStaked = useMemo(() => {
    return (
      (stakeMetadataAcc?.stakedAt !== undefined &&
        stakeMetadataAcc?.stakedAt !== null) ||
      !hasDelegateAuthority
    )
  }, [stakeMetadataAcc, hasDelegateAuthority])

  const readyToUnstake = useMemo(() => {
    if (!stakeMetadataAcc) return false
    // Check if nft was unstaked more than 30 days ago
    return (
      stakeMetadataAcc?.unstakedAt?.toNumber() * 1000 <
      Date.now() - 30 * 24 * 60 * 60 * 1000
    )
  }, [stakeMetadataAcc])

  const initializedUnstake = useMemo(() => {
    return (
      stakeMetadataAcc?.unstakedAt !== undefined &&
      stakeMetadataAcc?.unstakedAt !== null
    )
  }, [stakeMetadataAcc])

  const stake = useCallback(
    async (nft: PublicKey) => {
      const txn = new Transaction()

      const stakeIx = await auctionProgram.methods
        .stakeV0({})
        .accountsPartial({
          auctionManager: auctionKey,
          nft,
          stakeMetadata,
        })
        .instruction()

      txn.add(stakeIx)

      txn.feePayer = anchorProvider.publicKey
      txn.recentBlockhash = (
        await anchorProvider.connection.getLatestBlockhash()
      ).blockhash

      const signedTxn = await anchorProvider.wallet.signTransaction(txn)

      const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
        signedTxn.serialize(),
      ])

      if (!sigs || sigs.length === 0) {
        throw new Error('Transaction Error')
      }
    },
    [auctionProgram, auctionKey, stakeMetadata, anchorProvider],
  )

  const initializeUnstake = useCallback(
    async (nft: PublicKey) => {
      const txn = new Transaction()

      const stakeIx = await auctionProgram.methods
        .initializeUnstakeV0({})
        .accountsPartial({
          auctionManager: auctionKey,
          nft,
          stakeMetadata: stakeMetadataKey(nft, auctionKey)[0],
        })
        .instruction()

      txn.add(stakeIx)

      txn.feePayer = anchorProvider.publicKey
      txn.recentBlockhash = (
        await anchorProvider.connection.getLatestBlockhash()
      ).blockhash

      const signedTxn = await anchorProvider.wallet.signTransaction(txn)

      const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
        signedTxn.serialize(),
      ])

      if (!sigs || sigs.length === 0) {
        throw new Error('Transaction Error')
      }

      await fetchStakeMetadata()
    },
    [auctionProgram, auctionKey, anchorProvider, fetchStakeMetadata],
  )

  const unstake = useCallback(
    async (nft: PublicKey) => {
      const txn = new Transaction()

      const unstakeIx = await auctionProgram.methods
        .unstakeV0({})
        .accountsPartial({
          auctionManager: auctionKey,
          nft,
          stakeMetadata,
        })
        .instruction()

      txn.add(unstakeIx)

      txn.feePayer = anchorProvider.publicKey
      txn.recentBlockhash = (
        await anchorProvider.connection.getLatestBlockhash()
      ).blockhash

      const signedTxn = await anchorProvider.wallet.signTransaction(txn)

      const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
        signedTxn.serialize(),
      ])

      if (!sigs || sigs.length === 0) {
        throw new Error('Transaction Error')
      }

      await fetchStakeMetadata()
    },
    [
      auctionProgram,
      auctionKey,
      anchorProvider,
      stakeMetadata,
      fetchStakeMetadata,
    ],
  )

  const batchClaimRewards = useCallback(
    async (
      claims: Array<{
        nftName: string
        listing: PublicKey
        tokenMint: PublicKey
        endAt: number
      }>,
      onClaimAll?: () => void,
    ) => {
      try {
        const slicedClaims = claims.sort((a, b) => a.endAt - b.endAt)

        const allInstructions = await Promise.all(
          slicedClaims.map(async ({ nftName, listing, tokenMint, endAt }) => {
            const [meowMetadata] = meowMetadataKey(nft, auctionKey)

            let meowMetadataAcc
            try {
              meowMetadataAcc =
                await auctionProgram.account.meowMetadataV0.fetch(meowMetadata)
            } catch {}

            const [stakeReciept] = stakeRecieptKey(nft, listing)

            // Check if stake reciept exists
            let stakeRecieptAcc = null
            try {
              stakeRecieptAcc =
                await auctionProgram.account.stakeRecieptV0.fetch(stakeReciept)
            } catch {}

            return auctionProgram.methods
              .claimStakeRewardsV0({
                nftName,
                recipientWallet: anchorProvider.publicKey,
              })
              .accountsPartial({
                auctionManager: auctionKey,
                nft,
                stakeMetadata,
                meowMetadata: meowMetadataAcc ? meowMetadata : null,
                stakeReciept: stakeRecieptAcc ? stakeReciept : null,
                listing,
                tokenMint,
              })
              .instruction()
          }),
        )

        const transactions = await batchInstructionsToTxsWithPriorityFee(
          anchorProvider,
          allInstructions,
          { basePriorityFee: 0 },
        )

        for (const transaction of transactions) {
          toast({
            title: 'Claiming rewards...',
            description: 'Transaction is being processed on Solana',
            variant: 'default',
          })

          try {
            const signedTxn =
              await anchorProvider.wallet.signTransaction(
                transaction as unknown as Transaction,
              )
            const sigs = await bulkSendRawTransactions(
              anchorProvider.connection,
              [signedTxn.serialize() as Buffer],
            )

            if (!sigs || sigs.length === 0) {
              throw new Error('Transaction Error')
            }

            toast({
              title: 'Success!',
              description: 'Successfully claimed rewards',
              variant: 'default',
            })

            onClaimAll && onClaimAll()
          } catch (err) {
            toast({
              title: 'Failed to claim rewards',
              description: 'Please try again',
              variant: 'destructive',
              action: (
                <button
                  onClick={() => batchClaimRewards(claims, onClaimAll)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 px-3 py-2 rounded-md text-xs font-medium"
                >
                  Try Again
                </button>
              ),
            })
            throw err
          }
        }

        await fetchStakeMetadata()
      } catch (error) {
        console.error('Failed to process claim batch:', error)
        throw error
      }
    },
    [
      auctionProgram,
      anchorProvider,
      auctionKey,
      nft,
      stakeMetadata,
      fetchStakeMetadata,
      toast,
    ],
  )

  const claimRewards = useCallback(
    async (nftName: string, listing: PublicKey, tokenMint: PublicKey) => {
      const txn = new Transaction()

      const [meowMetadata] = meowMetadataKey(nft, auctionKey)

      let meowMetadataAcc
      try {
        meowMetadataAcc =
          await auctionProgram.account.meowMetadataV0.fetch(meowMetadata)
      } catch {}

      const [stakeReciept] = stakeRecieptKey(nft, listing)

      // Check if stake reciept exists
      let stakeRecieptAcc = null
      try {
        stakeRecieptAcc =
          await auctionProgram.account.stakeRecieptV0.fetch(stakeReciept)
      } catch {}

      const claimRewardsIx = await auctionProgram.methods
        .claimStakeRewardsV0({
          nftName,
          recipientWallet: anchorProvider.publicKey,
        })
        .accountsPartial({
          auctionManager: auctionKey,
          nft,
          stakeMetadata,
          meowMetadata: meowMetadataAcc ? meowMetadata : null,
          stakeReciept: stakeRecieptAcc ? stakeReciept : null,
          listing,
          tokenMint,
        })
        .instruction()

      txn.add(claimRewardsIx)

      txn.feePayer = anchorProvider.publicKey
      txn.recentBlockhash = (
        await anchorProvider.connection.getLatestBlockhash()
      ).blockhash

      const signedTxn = await anchorProvider.wallet.signTransaction(txn)

      const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
        signedTxn.serialize(),
      ])

      if (!sigs || sigs.length === 0) {
        throw new Error('Transaction Error')
      }

      await fetchStakeMetadata()
    },
    [
      auctionProgram,
      anchorProvider,
      auctionKey,
      nft,
      stakeMetadata,
      fetchStakeMetadata,
    ],
  )

  const getStakeReceipt = useCallback(
    async (listing: PublicKey) => {
      let reciept
      try {
        reciept = await auctionProgram.account.stakeRecieptV0.fetch(
          stakeRecieptKey(nft, listing)[0],
        )
      } catch {}
      return reciept
    },
    [auctionProgram, nft],
  )

  const timeLeftForCooldwon = useCallback(() => {
    const unstakedAt = stakeMetadataAcc?.unstakedAt
    if (!unstakedAt) return 0

    // Takes 30 days for cooldown period
    const cooldownPeriod = 30 * 24 * 60 * 60 * 1000 // 30 days in milliseconds

    const now = new Date()
    const unstakedAtDate = new Date(unstakedAt.toNumber() * 1000)
    const targetDate = new Date(unstakedAtDate.getTime() + cooldownPeriod)

    const diffTime = Math.max(0, targetDate.getTime() - now.getTime())

    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    const hours = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    )
    const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diffTime % (1000 * 60)) / 1000)

    return {
      days,
      hours,
      minutes,
      seconds,
      total: diffTime,
    }
  }, [stakeMetadataAcc])

  return {
    stake,
    initializeUnstake,
    unstake,
    isStaked,
    readyToUnstake,
    initializedUnstake,
    stakeMetadataLoading,
    claimRewards,
    batchClaimRewards,
    stakeMetadataAcc,
    getStakeReceipt,
    timeLeftForCooldwon,
  }
}

export default useStaking
